/**
 * Known-good ticket-code formats, copied verbatim from the greenscreen kiosks
 * (`green-screen/packages/kiosk-shared/src/ticketFormat.ts`). ENTR scans the
 * same physical tickets — predominantly the Fever 20-char QR — so it must apply
 * the same allow-list. Kept as a copy rather than a shared import because ENTR
 * is a standalone npm app and cannot resolve the pnpm-workspace package; if you
 * change a pattern here, change it there too.
 *
 * Derived empirically from 27k production sessions: a scanned code is a real
 * ticket only if it matches one of these patterns. Everything else is a wrong
 * scan — a product barcode, an exhibit-label QR (the SEA LIFE aquarium fish
 * signs), a waiver/marketing URL QR, or a truncated barcode — which the kiosk
 * used to silently turn into a junk "ticket not found" lookup.
 *
 * The set is a single bin checked at all kiosks (not per-venue): the formats
 * are distinct enough that cross-venue collisions are negligible, and a flat
 * allow-list needs no per-slug configuration.
 *
 *  - employee     EMPLOYEE-XXXXXXXX        self-issued staff code
 *  - fever         20 chars, alphabet 0-9 C D E F H J K M N P R T V W X Y
 *                  (Fever drops A B G I L O Q S U Z); 95–99.8% of Fever venues
 *  - london_bc     13-digit barcode         London's primary ticket
 *  - cologne_bc    24-digit GS1 barcode     Cologne wristband
 *  - cologne_short C + 8, no vowels         Cologne's second ticket system
 */

export const TICKET_PATTERNS: readonly RegExp[] = [
  // Employee codes are self-issued by our own frontend (the attract-screen
  // tap gesture), never scanned from a QR — so a customer can't forge one and
  // we accept any suffix. Prod carries two generators: the current
  // `EMPLOYEE-<8 hex>` (UUID slice) and a legacy `EMPLOYEE-<ms>-<suffix>`.
  /^EMPLOYEE-[A-Za-z0-9-]+$/,
  /^[0-9CDEFHJKMNPRTVWXY]{20}$/,
  /^[0-9]{13}$/,
  /^[0-9]{24}$/,
  /^[0-9BCDFGHJKLMNPQRSTVWXYZ]{9}$/,
];

// Scanners frequently append a trailing CR/LF/space from the keyboard-wedge.
export const normalizeTicketCode = (raw: string): string => raw.trim();

export const isKnownTicketFormat = (raw: string): boolean => {
  const code = normalizeTicketCode(raw);
  return TICKET_PATTERNS.some((pattern) => pattern.test(code));
};
